/**
 * Documentation registry (FRONTEND-FOLLOWUP-CONTRACT §C).
 *
 * Surfaces the canonical, implementation, and V3 spec markdown as a browsable,
 * in-app Documentation area.
 *
 * Loading is split for bundle size:
 *  - **Titles / grouping** come from `virtual:docs-manifest` — a tiny build-time
 *    metadata pass (slug + group + H1 title, no bodies) emitted by the
 *    `docsManifest` plugin in `vite.config.ts`. This keeps the list instant
 *    while adding only a few KB to the bundle.
 *  - **Bodies** are loaded *lazily*: `import.meta.glob(..., { query: "?raw" })`
 *    *without* `eager`, so each markdown file becomes its own code-split chunk
 *    fetched on demand (see {@link loadDocBody}) instead of inlining ~1 MB of
 *    markdown into the main JS chunk.
 *
 * The static Pages deploy still carries everything (chunks are emitted at build
 * time) — there is no runtime server read and no CORS. Path note: the Vite root
 * is `apps/portal`; this module lives at `apps/portal/src/lib/docs.ts`, and the
 * specs sit at the repo root — i.e. `../../../../specs` from here.
 */

// Build-time metadata (titles + grouping), no bodies. See vite.config.ts.
import { DOC_MANIFEST } from "virtual:docs-manifest";

// Lazy raw-markdown loaders, keyed by glob path. NOT eager: each file is a
// separate dynamic chunk, fetched only when its doc is opened.
const canonicalLoaders = import.meta.glob("../../../../specs/canonical/*.md", {
  query: "?raw",
  import: "default",
}) as Record<string, () => Promise<string>>;

const implementationLoaders = import.meta.glob(
  "../../../../specs/implementation/*.md",
  { query: "?raw", import: "default" },
) as Record<string, () => Promise<string>>;

const v3Loaders = import.meta.glob(
  [
    "../../../../specs/v3/*.md",
    "../../../../specs/v3/architecture/*.md",
    "../../../../specs/v3/canonical/*.md",
    "../../../../specs/v3/conceptual/*.md",
    "../../../../specs/v3/philosophical/*.md",
    "../../../../specs/v3/implementation/*.md",
    "../../../../specs/v3/product-technical/*.md",
    "../../../../specs/v3/screens/*.md",
    "../../../../specs/v3/technical/*.md",
    "../../../../specs/v3/topics/*.md",
  ],
  {
    query: "?raw",
    import: "default",
  },
) as Record<string, () => Promise<string>>;

const v3ScreenLoaders = import.meta.glob<string>(
  "../../../../specs/v3/screens/*.html",
  {
    query: "?raw",
    import: "default",
  },
);

const SCREEN_MOCKUP_GROUP = "V3 Screen Mockups" satisfies DocGroup;
type MockupLinkMode = "docs" | "standalone";

function docRouteHref(group: DocGroup, slug: string): string {
  return `/docs/${encodeURIComponent(`${group}:${slug}`)}`;
}

function routeWithSuffix(route: string, suffix: string): string {
  return `${route}${suffix}`;
}

function standaloneMockupHref(slug: string, suffix: string): string {
  return routeWithSuffix(`/mockups/v3/${encodeURIComponent(slug)}`, suffix);
}

function routedMockupHref(href: string, mode: MockupLinkMode): string | null {
  if (/^(?:[a-z][a-z0-9+.-]*:|#|\/)/i.test(href)) return null;

  const suffixStart = href.search(/[?#]/);
  const path = suffixStart === -1 ? href : href.slice(0, suffixStart);
  const suffix = suffixStart === -1 ? "" : href.slice(suffixStart);
  const normalizedPath = path.replace(/^\.\//, "");

  if (normalizedPath.endsWith(".html") && !normalizedPath.includes("/")) {
    const slug = normalizedPath.replace(/\.html$/i, "");
    const route = docRouteHref(SCREEN_MOCKUP_GROUP, slug);
    return mode === "standalone" ? standaloneMockupHref(slug, suffix) : `${route}${suffix}`;
  }

  if (normalizedPath.startsWith("../") && normalizedPath.endsWith(".md")) {
    const relativePath = normalizedPath.slice("../".length).replace(/\.md$/i, "");
    const group = groupFromV3RelativePath(relativePath);
    return `${docRouteHref(group, v3SlugFromRelativePath(relativePath))}${suffix}`;
  }

  return null;
}

function rewriteMockupLinks(html: string, mode: MockupLinkMode): string {
  return html.replace(
    /\shref=(["'])([^"']+)\1/g,
    (source, quote: string, href: string) => {
      const route = routedMockupHref(href, mode);
      return route
        ? ` href=${quote}${route}${quote} target=${quote}_parent${quote}`
        : source;
    },
  );
}

const screenMockupLoaders = loaderMapFromPaths(
  v3ScreenLoaders,
  screenSlugFromPath,
);

export function docRoutePath(doc: Pick<DocEntry, "group" | "slug">): string {
  return docRouteHref(doc.group, doc.slug);
}

export function standaloneMockupRoutePath(doc: Pick<DocEntry, "group" | "slug">): string {
  return standaloneMockupHref(doc.slug, "");
}

export async function loadScreenMockupHtml(
  doc: DocEntry,
  mode: MockupLinkMode = "docs",
): Promise<string | null> {
  if (doc.assetKind !== "html-mockup") return null;
  const load = screenMockupLoaders[doc.slug];
  if (!load) return null;
  return rewriteMockupLinks(await load(), mode);
}

type ManifestDocGroup = "Canonical" | "Implementation";

export type DocGroup = ManifestDocGroup | `V3 ${string}`;

export interface DocEntry {
  /** Filename without extension, e.g. `01-vision-and-principles`. URL slug. */
  slug: string;
  title: string;
  group: DocGroup;
  /** Leading numeric prefix (`01-…` → 1), else 999 so prose docs sort by name. */
  order: number;
  assetKind?: "html-mockup";
}

/** `01-vision-and-principles.md` → `01-vision-and-principles`. */
function slugFromPath(path: string): string {
  const file = path.split("/").pop() ?? path;
  return file.replace(/\.md$/i, "");
}

/** Leading numeric prefix → its number; otherwise 999 (sorts after numbered). */
function orderFromSlug(slug: string): number {
  const m = /^(\d+)/.exec(slug);
  return m ? Number(m[1]) : 999;
}

function titleWord(token: string): string {
  const lower = token.toLowerCase();
  if (/^\d+$/.test(lower)) return lower;
  if (["api", "mcp", "okr", "rbac", "ui", "ux", "v3"].includes(lower)) {
    return lower.toUpperCase();
  }
  return `${lower.slice(0, 1).toUpperCase()}${lower.slice(1)}`;
}

function titleFromSlug(slug: string): string {
  const normalized = slug.replace(/\.md$/i, "").replace(/^\d+-/, "");
  const words = normalized.split(/[-_]+/).filter(Boolean);
  return words.length > 0 ? words.map(titleWord).join(" ") : slug;
}

function v3RelativeFromPath(path: string): string {
  const marker = "specs/v3/";
  const start = path.indexOf(marker);
  const relative = start === -1 ? path : path.slice(start + marker.length);
  return relative.replace(/\.md$/i, "");
}

function v3SlugFromRelativePath(relativePath: string): string {
  return relativePath.replace(/\//g, "--");
}

function basenameFromRelativePath(relativePath: string): string {
  const parts = relativePath.split("/");
  return parts[parts.length - 1] ?? relativePath;
}

function screenSlugFromPath(path: string): string {
  const file = path.split("/").pop() ?? path;
  return file.replace(/\.html$/i, "");
}

const V3_SECTION_GROUPS: Record<string, DocGroup> = {
  architecture: "V3 Architecture",
  canonical: "V3 Canonical",
  conceptual: "V3 Conceptual",
  philosophical: "V3 Philosophical",
  implementation: "V3 Implementation",
  "product-technical": "V3 Product",
  screens: "V3 Screens",
  technical: "V3 Technical",
  topics: "V3 Topics",
};

function groupFromV3RelativePath(relativePath: string): DocGroup {
  if (!relativePath.includes("/")) return "V3 Overview";
  const section = relativePath.split("/")[0] ?? "";
  return V3_SECTION_GROUPS[section] ?? `V3 ${titleFromSlug(section)}`;
}

const V3_TITLE_OVERRIDES: Record<string, string> = {
  "V3 Overview:README": "Agentic OS V3 Specification",
  "V3 Overview:provenance": "V3 Provenance",
  "V3 Conceptual:README": "Conceptual Ontology",
  "V3 Product:README": "Product-Technical Specification",
  "V3 Screens:README": "V3 Screen Mockups",
  "V3 Technical:README": "Technical Specification",
  "V3 Canonical:V3-OVERVIEW": "Canonical V3 Overview",
};

function titleFromV3Path(relativePath: string, group: DocGroup): string {
  const fileSlug = basenameFromRelativePath(relativePath);
  return V3_TITLE_OVERRIDES[`${group}:${fileSlug}`] ?? titleFromSlug(fileSlug);
}

function loaderMapFromPaths(
  loaders: Record<string, () => Promise<string>>,
  slugForPath: (path: string) => string,
): Record<string, () => Promise<string>> {
  return Object.fromEntries(
    Object.entries(loaders).map(([path, load]) => [slugForPath(path), load]),
  );
}

function buildV3LoaderGroups(): Partial<
  Record<DocGroup, Record<string, () => Promise<string>>>
> {
  const groups: Partial<Record<DocGroup, Record<string, () => Promise<string>>>> =
    {};
  for (const [path, load] of Object.entries(v3Loaders)) {
    const relativePath = v3RelativeFromPath(path);
    const group = groupFromV3RelativePath(relativePath);
    const groupLoaders = groups[group] ?? {};
    groupLoaders[v3SlugFromRelativePath(relativePath)] = load;
    groups[group] = groupLoaders;
  }
  return groups;
}

const DOC_GROUP_ORDER: readonly DocGroup[] = [
  "Canonical",
  "Implementation",
  "V3 Overview",
  "V3 Conceptual",
  "V3 Philosophical",
  "V3 Product",
  "V3 Technical",
  "V3 Architecture",
  "V3 Canonical",
  "V3 Implementation",
  "V3 Topics",
  "V3 Screens",
  "V3 Screen Mockups",
];

function groupRank(group: DocGroup): number {
  const rank = DOC_GROUP_ORDER.indexOf(group);
  return rank === -1 ? 999 : rank;
}

/**
 * Per-group `slug → loader` maps. The lazy glob keys are full paths; we re-key
 * by slug so {@link loadDocBody} can resolve a body from a {@link DocEntry}.
 */
const LOADERS: Partial<Record<DocGroup, Record<string, () => Promise<string>>>> = {
  Canonical: loaderMapFromPaths(canonicalLoaders, slugFromPath),
  Implementation: loaderMapFromPaths(implementationLoaders, slugFromPath),
  ...buildV3LoaderGroups(),
};

function buildManifestGroup(group: ManifestDocGroup): DocEntry[] {
  return DOC_MANIFEST.filter((d) => d.group === group)
    .map(
      (d) =>
        ({
          slug: d.slug,
          title: d.title,
          group,
          order: orderFromSlug(d.slug),
        }) satisfies DocEntry,
    )
    .sort((a, b) => a.order - b.order || a.slug.localeCompare(b.slug));
}

function buildV3Docs(): DocEntry[] {
  return Object.keys(v3Loaders)
    .map((path) => {
      const relativePath = v3RelativeFromPath(path);
      const fileSlug = basenameFromRelativePath(relativePath);
      const group = groupFromV3RelativePath(relativePath);
      return {
        slug: v3SlugFromRelativePath(relativePath),
        title: titleFromV3Path(relativePath, group),
        group,
        order: orderFromSlug(fileSlug),
      } satisfies DocEntry;
    })
    .sort(
      (a, b) =>
        groupRank(a.group) - groupRank(b.group) ||
        a.order - b.order ||
        a.slug.localeCompare(b.slug),
    );
}

function buildScreenMockupDocs(): DocEntry[] {
  return Object.keys(v3ScreenLoaders)
    .map((path) => {
      const slug = screenSlugFromPath(path);
      return {
        slug,
        title: slug === "index" ? "Screen Mockup Index" : titleFromSlug(slug),
        group: SCREEN_MOCKUP_GROUP,
        order: slug === "index" ? 0 : orderFromSlug(slug),
        assetKind: "html-mockup",
      } satisfies DocEntry;
    })
    .sort((a, b) => a.order - b.order || a.slug.localeCompare(b.slug));
}

/**
 * All docs, stable section order first then filename order. Slugs are unique
 * within a group; cross-group collisions (e.g. README docs) are disambiguated
 * by the group prefix in the route — see `getDoc`, which matches
 * group-qualified slugs first.
 */
export const DOCS: DocEntry[] = [
  ...buildManifestGroup("Canonical"),
  ...buildManifestGroup("Implementation"),
  ...buildV3Docs(),
  ...buildScreenMockupDocs(),
];

/** The grouped form for the left list (preserves group order + intra sort). */
const orderedGroups = DOC_GROUP_ORDER.filter((group) =>
  DOCS.some((doc) => doc.group === group),
);

const extraGroups = DOCS.map((doc) => doc.group)
  .filter(
    (group, index, groups) =>
      !DOC_GROUP_ORDER.includes(group) && groups.indexOf(group) === index,
  )
  .sort((a, b) => a.localeCompare(b));

export const DOC_GROUPS: { group: DocGroup; docs: DocEntry[] }[] = [
  ...orderedGroups,
  ...extraGroups,
].map((group) => ({
  group,
  docs: DOCS.filter((doc) => doc.group === group),
}));

/**
 * Resolve a slug to a doc. Several sections carry README docs; to keep links
 * stable we accept either a bare slug (first match wins by section order) or a
 * `group:slug` qualified form.
 */
export function getDoc(slug: string): DocEntry | undefined {
  if (!slug) return DOCS[0];
  const sep = slug.indexOf(":");
  if (sep !== -1) {
    const group = slug.slice(0, sep);
    const bare = slug.slice(sep + 1);
    return DOCS.find((d) => d.group === group && d.slug === bare);
  }
  return DOCS.find((d) => d.slug === slug);
}

/**
 * Lazily load a doc's raw markdown body. Resolves the per-group `slug → loader`
 * map and awaits the dynamic `?raw` import (its own code-split chunk). Returns
 * `null` if no loader exists for the entry (should not happen, since manifest
 * and loaders derive from the same files).
 */
export async function loadDocBody(doc: DocEntry): Promise<string | null> {
  if (doc.assetKind === "html-mockup") return null;
  const load = LOADERS[doc.group]?.[doc.slug];
  if (!load) return null;
  return load();
}

/** The default doc shown at `/docs` with no slug (first canonical doc). */
export const DEFAULT_DOC: DocEntry | undefined = DOCS[0];
